import { forwardRef, useMemo, useState } from 'react';

import { RiCalendarLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  DateInput as MantineDateInput,
  DateInputProps as MantineDateInputProps
} from '@mantine/dates';
import { dayjs } from '@ui';
import { parseDateInput, preventInvalidDateInput } from '@utils';

import { Badge, Indicator } from '../../datadisplay';
import { useMergeClassNames } from '../../utils';

import dateInputClasses from './DateInput.module.css';
import { DateInputOverrideClassNames } from './DateInput.styles';
// Helper function to convert string dates to Date objects
const parseStringToDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.toDate() : null;
  }
  return null;
};

// Enhanced props interface for string date support
interface DateInputWithStringProps
  extends Omit<MantineDateInputProps, 'value' | 'onChange'> {
  value?: Date | string | null;
  onChange?: (value: Date | null) => void;
  hideDaysCount?: boolean; // Optional prop to hide the days count
}

/**
 * Editable date input
 * Use DatePicker input to prevent user from editing, it also support range, multi select
 */
const DateInput = forwardRef<HTMLInputElement, DateInputWithStringProps>(
  (
    {
      size = 'md',
      error,
      leftSection,
      rightSection,
      valueFormat = 'DD/MM/YYYY',
      classNames,
      defaultLevel,
      onFocus,
      onBlur,
      value,
      onChange,
      hideDaysCount = false,
      ...rest
    },
    ref
  ) => {
    const [focussed, setFocussed] = useState<boolean>(false);

    // Parse the incoming value to a Date object for Mantine
    const parsedValue = useMemo(() => parseStringToDate(value), [value]);

    // show elapsed time since the date
    const Elapsed = useMemo(() => {
      if (hideDaysCount || !parsedValue) return null;

      const noOfYearsToDate = dayjs().diff(dayjs(value), 'years');
      const noOfDaysToDate = dayjs().diff(dayjs(value), 'days');
      let elapsedText = null;
      if (noOfYearsToDate > 0) {
        elapsedText = `${noOfYearsToDate} Year${noOfYearsToDate > 1 ? 's' : ''}`;
      } else if (noOfDaysToDate > 0) {
        elapsedText = `${noOfDaysToDate} Day${noOfDaysToDate > 1 ? 's' : ''}`;
      } else if (dayjs(value).isToday()) {
        elapsedText = 'Today';
      }
      return elapsedText ? (
        <Badge size="sm" color="secondary" variant="light" radius="xs">
          {elapsedText}
        </Badge>
      ) : null;
    }, [value, parsedValue, hideDaysCount]);

    const rightSectionFinal = error ? (
      <RiErrorWarningLine />
    ) : rightSection ? (
      rightSection
    ) : !hideDaysCount ? (
      Elapsed
    ) : null;

    const classes = useMemo(() => {
      return DateInputOverrideClassNames({
        size,
        focussed,
        error,
        rightSection: rightSectionFinal,
        largeRightSection: rightSectionFinal === Elapsed
      });
    }, [size, focussed, error, rightSectionFinal, Elapsed]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(true);
      onFocus && onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(false);
      onBlur && onBlur(e);
    };

    // Handle value changes from the date input
    const handleDateChange = (newDate: Date | null) => {
      if (onChange) {
        onChange(newDate);
      }
    };

    const mergedClassNames = useMergeClassNames({
      classNames: [classes, classNames]
    });

    return (
      <MantineDateInput
        {...rest}
        ref={ref}
        leftSection={leftSection ? leftSection : <RiCalendarLine />}
        size={size}
        error={error}
        rightSection={rightSectionFinal}
        valueFormat={valueFormat}
        value={parsedValue}
        onChange={handleDateChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        defaultLevel={parsedValue instanceof Date ? 'month' : defaultLevel}
        renderDay={(date) => {
          const isToday = dayjs(date).isToday();
          return (
            <Indicator
              position="bottom-center"
              disabled={!isToday}
              classNames={{
                indicator: dateInputClasses.indicator
              }}
            >
              <div>{date.getDate()}</div>
            </Indicator>
          );
        }}
        classNames={mergedClassNames}
        dateParser={parseDateInput}
        onKeyDown={preventInvalidDateInput}
      />
    );
  }
);

export { DateInput };
export type { DateInputWithStringProps as DateInputProps };
